import { cleverTapCommonWords } from "../../analytics/clevertapConstants";
import { COUNTRIES } from "../../constants/common/common";
import { HDFC_SMARTBUY, PARTNER } from "../../constants/storage";
import { productTypes } from "../../constants/pageConstants/pdpConstants";
import CookieUtil from "../storage/cookieUtil";
import { getPersonalizationCompletionAnalytics, hasStartedTextPersonalization } from "../pdp/personalization";

/**
 * Resolves HDFC SmartBuy user flag from an explicit value or partner cookie.
 *
 * @param {boolean} [isSmartBuyUser]
 * @returns {boolean}
 */
const resolveIsSmartBuyUser = (isSmartBuyUser) => {
  if (typeof isSmartBuyUser === "boolean") {
    return isSmartBuyUser;
  }
  return CookieUtil.getCookie(PARTNER) === HDFC_SMARTBUY;
};

/**
 * Builds the shared CleverTap payload for Customize Now Clicked / Customization Done.
 *
 * @param {Object} params
 * @param {Object} [params.baseData] - Existing PDP CleverTap fields (common / page / PDP event data).
 * @param {Object} [params.currentItem] - Current PDP product.
 * @param {boolean} [params.isOutOfStock]
 * @param {boolean} [params.isSameSku]
 * @param {boolean} [params.isInternational]
 * @param {Object} [params.deliveryDates]
 * @param {string} [params.nextAvailableDateDiff]
 * @param {string} [params.productUrl]
 * @param {boolean} [params.isSmartBuyUser]
 * @param {boolean} [params.includeUrls] - Include complete_url / clean_url (Customize Now Clicked).
 * @param {boolean} [params.includePersonalizationStats] - Include is_text / is_image counts (Customization Done).
 * @returns {Object}
 */
export const buildCustomizationCleverTapPayload = ({
  baseData = {},
  currentItem,
  isOutOfStock = false,
  isSameSku = false,
  isInternational = false,
  deliveryDates,
  nextAvailableDateDiff = "",
  productUrl = "",
  isSmartBuyUser,
  includeUrls = false,
  includePersonalizationStats = false,
} = {}) => {
  const catalogIsIndia = currentItem?.catalogId === COUNTRIES.INDIA;
  const resolvedInternational = baseData.is_international ?? isInternational;
  const isCakeMessage =
    currentItem?.primaryCategoryId === productTypes.CAKE &&
    hasStartedTextPersonalization(currentItem?.productId, currentItem?.personalizedInfo);

  const payload = {
    is_template_available: Boolean(currentItem?.hasPersonalizableUI),
    is_cake_message: isCakeMessage,
    page_type: baseData.page_type || cleverTapCommonWords.PDP,
    page_name: baseData.page_name,
    product_name: baseData.product_name,
    pid: baseData.pid,
    product_category: baseData.product_category,
    is_variant_available: baseData.is_variant_available,
    is_egg: baseData.is_egg,
    delivery_type: baseData.delivery_type,
    product_price: baseData.product_price,
    currency: baseData.currency,
    strike_price: baseData.strike_price,
    discount_percentage: baseData.discount_percentage,
    is_product_available: baseData.is_product_available,
    is_international: resolvedInternational,
    city: baseData.city,
    area: baseData.area,
    pincode: baseData.pincode,
    system_pincode: baseData.system_pincode,
    country: baseData.country,
    is_want_same_day_visible:
      baseData.is_want_same_day_visible ??
      (catalogIsIndia && !isOutOfStock && deliveryDates?.deliveryDates?.length > 0),
    is_same_sku: baseData.is_same_sku ?? isSameSku,
    product_rating: baseData.product_rating,
    product_review_count: baseData.product_review_count,
    sku_id: baseData.sku_id,
    login_status: baseData.login_status,
    image_url: baseData.image_url,
    next_available_date_difference: resolvedInternational
      ? ""
      : nextAvailableDateDiff || baseData.next_available_date_difference || "",
    product_url: productUrl || baseData.product_url || baseData.clean_url || "",
    is_smartbuy_user: resolveIsSmartBuyUser(isSmartBuyUser),
  };

  if (includeUrls) {
    payload.complete_url = baseData.complete_url;
    payload.clean_url = baseData.clean_url;
  }

  if (includePersonalizationStats) {
    Object.assign(payload, getPersonalizationCompletionAnalytics(currentItem));
  }

  return payload;
};
